import dayjs from "dayjs";
import { LocationDescriptorObject } from "history";
import i18next from "i18next";
import moment from "moment";

import { IconName } from "@arbolus-technologies/antDComponents";
import { Country, WorkHistoryUpdate } from "@arbolus-technologies/api";
import { EmployedStatus } from "@arbolus-technologies/models/common";
import {
  CUSTOMER_INSIGHT_ANSWER_STATUS,
  CUSTOMER_PERSONA,
  CUSTOMER_SPEND_QUESTION,
  InsightsFormInterface
} from "@arbolus-technologies/models/customers";
import {
  EXPERT_APPLICATION_STEPS,
  MANDATORY_PAST_WORK_HISTORY_MONTHS,
  TimeGap,
  WorkHistory
} from "@arbolus-technologies/models/expert";

import { SPEND_CONSTANTS } from "@arbolus-technologies/models/customer-library";
import { convertValueToCurrencyFormat } from "./Currency";
import {
  getFirstDayOfLastMonth,
  getFirstDayOfMonth,
  getThirdDayOfTheMonth,
  monthsBetweenDates,
  monthsFromDate
} from "./Date";

export const markApplicationSubmitted = (
  shouldSubmit: boolean,
  completeApplicationStep: (
    completedStepName: EXPERT_APPLICATION_STEPS,
    submitApplication: boolean
  ) => void
): void => {
  if (shouldSubmit) {
    completeApplicationStep(
      EXPERT_APPLICATION_STEPS.APPLICATION_SUBMITTED,
      false
    );
  }
};

export const maxLengthString = (text: string, maxLength: number): string =>
  text.length >= maxLength
    ? text.substring(0, maxLength) + String.fromCharCode(8230)
    : text;

export const fullName = (name: string, lastName: string): string =>
  `${name.trim()} ${lastName.trim()}`;

export const getFullNameOrEmailOrId = (
  id?: string | null,
  email?: string | null,
  firstName?: string | null,
  lastName?: string | null
): string => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  if (fullName && fullName !== "") {
    return fullName;
  }
  return email ?? id ?? i18next.t("common:utils:notDefined");
};

export const emptyParagraphTag = "<p></p>";

export const displayUserName = (
  user: Partial<{
    firstName: string;
    lastName: string;
    email: string;
    fullName: string;
  }>,
  isFullName = true
): string => {
  const { firstName, lastName, email, fullName } = user || {};
  if (fullName) return fullName;
  if (isFullName) {
    return firstName && lastName
      ? `${firstName} ${lastName}`
      : (firstName ?? lastName ?? email ?? "");
  }
  return firstName ?? lastName ?? email ?? "";
};

export const displayTotalAmount = (
  isCredit: boolean,
  totalProjectSpent: {
    amount: number;
    currencyCode: string | null;
    minimumFractionDigits?: number;
  }
): string => {
  if (isCredit) {
    return `${Number(totalProjectSpent.amount).toFixed(2)} ${i18next.t(
      "common:Credit:credit",
      {
        count: totalProjectSpent.amount
      }
    )}`;
  }

  if (totalProjectSpent.currencyCode) {
    return convertValueToCurrencyFormat(
      totalProjectSpent.amount,
      totalProjectSpent.currencyCode,
      totalProjectSpent.minimumFractionDigits
    );
  }

  return "";
};

interface HighlightWords {
  text: string;
  tags: Array<string | undefined>;
  highlightColor?: string;
  boldedWords?: boolean;
}

export const highlightWords = (highlightWords: HighlightWords): string => {
  const {
    tags,
    text,
    highlightColor = "#96BFFF",
    boldedWords = false
  } = highlightWords;
  if (text.length === 0 || tags.length === 0) return text;

  const escapedTags = tags
    .filter((tag) => tag !== undefined)
    .map((tag) => tag?.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
    .join("|");

  const tagRegex = new RegExp(`(?:\\b|,)(${escapedTags})(?:\\b|,)?`, "gi");

  const highlightedText = text.replace(tagRegex, (match) => {
    const sanitizedMatch = match.replace(/\\/g, ""); // Remove any backslashes introduced
    return `<mark style="background-color:${highlightColor}; font-weight:${
      boldedWords ? 600 : "inherit"
    }">${sanitizedMatch}</mark>`;
  });

  return highlightedText;
};

export const isValueTruthy = <TValue>(
  value: TValue | null | undefined
): value is TValue => value !== null && value !== undefined;

export const locationObjectToString = (
  location: LocationDescriptorObject
): string => {
  const { pathname, search, hash } = location;
  const queryString = search
    ? `?${new URLSearchParams(search).toString()}`
    : "";

  // Include the hash in the final string if it exists
  const hashString = hash ? `#${hash}` : "";

  return `${pathname}${queryString}${hashString}`;
};

export const extractBoolEnvVariable = (
  envVariable: string
): boolean | undefined => {
  const variableValue = process.env[envVariable];
  if (typeof variableValue !== "string") return undefined;
  return JSON.parse(variableValue);
};

export const DISABLE_MIXPANEL =
  extractBoolEnvVariable("NX_PUBLIC_DISABLE_MIXPANEL") === true;

const BAIN_CLIENT_ID = process.env["NX_PUBLIC_BAIN_ID"];
export function isBainClient(clientId: string): boolean {
  return clientId === BAIN_CLIENT_ID;
}

// Left for compatibility with old components (ciq-admin and ciq-client legacy)
export const WORKSPACE_TYPES = {
  WORKSPACE: "Workspace",
  CLIENT_SUPPORT: "ClientSupport",
  EXPERT_SUPPORT: "ExpertSupport"
};

export const joinStringsWithSeparator = (
  inputArray: (string | null | undefined)[],
  separator = "-"
): string => inputArray.filter((value) => value != null).join(` ${separator} `);

export function initialsFromFullName(fullName: string) {
  let initials = fullName;
  const nameSplit = initials.split(/\s/);
  // Only in case name is more than one word
  if (nameSplit.length > 1) {
    initials = nameSplit
      .map((part) => part.substring(0, 1).toUpperCase())
      .filter((v) => !!v)
      .slice(0, 2)
      .join("");
  } else {
    initials = initials.substring(0, 1).toUpperCase();
  }
  return initials;
}

export const getMetadataCountryFromThreeLetterCode = (
  metadataCountries: Country[],
  threeLetterCode?: string
) => {
  return metadataCountries.find(
    (metadataCountry) => metadataCountry.threeLetterCode === threeLetterCode
  );
};

export const formatDateToUTC = (
  date?: Date | string | moment.Moment | null
): string | undefined => {
  if (!date) return;

  // If it's a Moment object, convert to UTC ISO string
  if (moment.isMoment(date)) {
    return encodeURIComponent(date.utc().toISOString());
  }

  // Convert to Date and ensure it's valid
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return;

  return encodeURIComponent(parsedDate.toISOString());
};

export const getMissingTimeGaps = (workHistory: WorkHistory[]): TimeGap[] => {
  const workHistoryItemDurations = workHistory.map((wh) => ({
    from: wh.from,
    to: wh.to ?? getFirstDayOfMonth()
  }));

  const firstDayOfTheMonth = getFirstDayOfMonth();
  const firstDayOfLastMonth = getFirstDayOfLastMonth();
  const mandatoryPastWorkHistoryDate = monthsFromDate(
    firstDayOfTheMonth,
    -MANDATORY_PAST_WORK_HISTORY_MONTHS
  );

  const missingGaps: TimeGap[] = [];

  let lowerThreshold = mandatoryPastWorkHistoryDate;
  const filteredHistory = workHistoryItemDurations
    .filter((wh) => wh.to === null || wh.to >= mandatoryPastWorkHistoryDate)
    .sort((a, b) => a.from.getTime() - b.from.getTime());

  if (filteredHistory.length === 0) {
    missingGaps.push({
      from: mandatoryPastWorkHistoryDate,
      to: firstDayOfTheMonth
    });
    return missingGaps;
  }

  for (const workHistoryItem of filteredHistory) {
    const monthsDiff = monthsBetweenDates(workHistoryItem.from, lowerThreshold);

    if (monthsDiff > 2) {
      missingGaps.push({ from: lowerThreshold, to: workHistoryItem.from });
    }

    if (
      workHistoryItem.to === null ||
      workHistoryItem.to >= firstDayOfLastMonth
    ) {
      break;
    }

    if (workHistoryItem === filteredHistory[filteredHistory.length - 1]) {
      const monthsFromLastToNow = monthsBetweenDates(
        firstDayOfTheMonth,
        workHistoryItem.to
      );

      if (monthsFromLastToNow > 2) {
        missingGaps.push({ from: workHistoryItem.to, to: firstDayOfTheMonth });
      }
    }

    lowerThreshold = workHistoryItem.to!;
  }

  return missingGaps;
};

export const mapWorkHistoryToModel = (item: WorkHistory): WorkHistory => ({
  id: item.id,
  employedStatus: item.employedStatus,
  title: item.title,
  jobDescription: item.jobDescription,
  from: new Date(item.from),
  to: item.to ? new Date(item.to) : null,
  isCurrentWork: item.isCurrentWork,
  company: item.company
    ? {
        name: item.company.name,
        id: item.company.id
      }
    : null,
  country: item.country
    ? {
        threeLetterCode: item.country.isoCountryCode,
        name: item.country.name
      }
    : null
});

// Any current position can back a software review. Company-backed positions
// (Employed/Founder) attribute to that company; the company-less types
// (Independent, Unemployed, Retired) are routed by the backend to a centralized
// company per employment status. A position is therefore reviewable whenever it
// has a company or a known employment status; only malformed rows with neither
// stay ineligible.
export const isReviewableWorkHistory = (workHistory: WorkHistory): boolean =>
  Boolean(workHistory.company?.id) || workHistory.employedStatus != null;

// The label for a work history's role: its job title, or the employment-type label
// for company-less roles (freelance/unemployed/retired) that have no title.
// translateEmploymentType maps an EmployedStatus to its label (the
// "employmentTypeSelector" i18n namespace, where the key is the enum value).
export const getWorkHistoryRoleLabel = (
  workHistory: WorkHistory,
  translateEmploymentType: (status: EmployedStatus) => string
): string => {
  if (workHistory.title?.trim()) {
    return workHistory.title;
  }
  if (workHistory.employedStatus) {
    return translateEmploymentType(workHistory.employedStatus);
  }
  return "";
};

// The full display label for a work history: "Role · Company", where Role is the
// job title (or the employment-type label for company-less roles) and Company is
// the real company name only when present. Company-less roles therefore show just
// the role (e.g. "Retired", "Freelance/Self-employed"), never a redundant suffix.
export const getWorkHistoryDisplayLabel = (
  workHistory: WorkHistory,
  translateEmploymentType: (status: EmployedStatus) => string
): string =>
  [
    getWorkHistoryRoleLabel(workHistory, translateEmploymentType),
    workHistory.company?.name
  ]
    .filter(Boolean)
    .join(" · ");

export const mapModelToWorkHistoryUpdate = (
  model: WorkHistory
): WorkHistoryUpdate => ({
  companyId:
    model.employedStatus === EmployedStatus.Independent
      ? null
      : model.company?.id,
  title: model.title,
  jobDescription: model.jobDescription,
  from: getThirdDayOfTheMonth(model.from),
  to: model.to && !model.isCurrentWork ? getThirdDayOfTheMonth(model.to) : null,
  isCurrentWork: model.isCurrentWork || model.to === null,
  isoCountryCode: model.country?.threeLetterCode,
  employedStatus: model.employedStatus
});

export const orderWorkHistoryItems = (
  a: WorkHistory,
  b: WorkHistory
): number => {
  if (!a.to && b.to) {
    return -1;
  }

  if (a.to && !b.to) {
    return 1;
  }

  if (!a.to && !b.to) {
    return a.from.getTime() >= b.from.getTime() ? -1 : 1;
  }

  return a.to!.getTime() >= b.to!.getTime() ? -1 : 1;
};

export const getSpendValue = (spend: number) => {
  if (spend < SPEND_CONSTANTS.SMALL_THRESHOLD) {
    return "small";
  }
  if (spend > SPEND_CONSTANTS.LARGE_THRESHOLD) {
    return "large";
  }

  return "mid";
};

export const formatStringWithDates = (
  initialString: string,
  from: string,
  to: string | null,
  dateFormat: string,
  separator = "-"
): string => {
  const formattedFrom = dayjs(from).format(dateFormat);
  const formattedTo = to
    ? dayjs(to).format(dateFormat)
    : i18next.t("common:current");
  return `${initialString} ${separator} ${formattedFrom} - ${formattedTo}`;
};

export const INSIGHT_STATUS_MAP: Record<
  CUSTOMER_INSIGHT_ANSWER_STATUS,
  { color: string; icon: IconName }
> = {
  [CUSTOMER_INSIGHT_ANSWER_STATUS.Pending]: {
    color: "warning",
    icon: "error"
  },
  [CUSTOMER_INSIGHT_ANSWER_STATUS.InReview]: {
    color: "processing",
    icon: "sync"
  },
  [CUSTOMER_INSIGHT_ANSWER_STATUS.Approved]: {
    color: "success",
    icon: "check_circle"
  },
  [CUSTOMER_INSIGHT_ANSWER_STATUS.Rejected]: {
    color: "error",
    icon: "cancel"
  },
  [CUSTOMER_INSIGHT_ANSWER_STATUS.Declined]: {
    color: "purple",
    icon: "block"
  },
  [CUSTOMER_INSIGHT_ANSWER_STATUS.Outdated]: {
    color: "default",
    icon: "history"
  }
};

export const isCountableReviewStatus = (
  status: CUSTOMER_INSIGHT_ANSWER_STATUS | null | undefined
): boolean => {
  if (!status) {
    return false;
  }
  return (
    status === CUSTOMER_INSIGHT_ANSWER_STATUS.InReview ||
    status === CUSTOMER_INSIGHT_ANSWER_STATUS.Approved
  );
};

/**
 * Normalizes a URL by ensuring it has a protocol prefix.
 * If the URL already starts with "http", it returns as-is.
 * Otherwise, it prepends "https://".
 */
export const normalizeUrl = (url: string): string => {
  if (url.startsWith("http")) {
    return url;
  }
  return `https://${url}`;
};

/**
 * Returns the normalized URL only when it resolves to an http(s) scheme.
 * Guards against dangerous schemes (javascript:, data:, vbscript:, etc.)
 * before the value is rendered into an anchor href. Returns undefined when
 * the input is unsafe or not a valid URL.
 */
export const getSafeHttpUrl = (url: string): string | undefined => {
  try {
    const parsedUrl = new URL(normalizeUrl(url));
    if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
      return parsedUrl.toString();
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export const buildInsightsPayload = (
  values: InsightsFormInterface,
  persona: CUSTOMER_PERSONA
): InsightsFormInterface => {
  const payload: InsightsFormInterface = {
    persona: values.persona,
    personaDetails: values.personaDetails,
    implementationStage: values.implementationStage,
    nps: values.nps,
    npsDetails: values.npsDetails,
    competitors: values.competitors,
    otherComments: values.otherComments
  };

  if (persona === CUSTOMER_PERSONA.Influencer) {
    payload.spendQuestion = CUSTOMER_SPEND_QUESTION.Yes;
    payload.spend = values.spend;
    payload.spendDetails = values.spendDetails;
    payload.renewalIntent = values.renewalIntent;
    payload.renewalDetails = values.renewalDetails;
    payload.purchaseDetails = values.purchaseDetails;
    payload.usageDetails = values.usageDetails;
  } else {
    payload.spendQuestion = CUSTOMER_SPEND_QUESTION.No;
  }

  return payload;
};
